import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Loader2, Pencil, Trash2, GripVertical, Download } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import {
  useAllShortcuts,
  useUpsertShortcut,
  useDeleteShortcut,
  useReorderShortcuts,
  type DentalShortcutRow,
} from '@/hooks/useDentalShortcuts';
import { useProcedureCodes } from '@/hooks/useProcedureCodes';
import { ShortcutEditModal } from '@/components/dental/shortcuts/ShortcutEditModal';

// ── Default seed set (for "Importér standardsæt") ────────────────────────────

const DEFAULT_SHORTCUTS = [
  { label: 'UNDS', code: '1114', color: '#10b981' },
  { label: 'DG', code: '1112', color: '#059669' },
  { label: 'BW 2', code: '1150', color: '#22c55e' },
  { label: 'Komposit 1F', code: '1505', color: '#3b82f6' },
  { label: 'Komposit 2F', code: '1506', color: '#2563eb' },
  { label: 'Komposit 3F', code: '1507', color: '#1d4ed8' },
  { label: 'Komposit 4F+', code: '1509', color: '#1e3a8a' },
  { label: 'RCT for', code: '1605', color: '#f97316' },
  { label: 'RCT bag', code: '1601', color: '#ea580c' },
  { label: 'Krone', code: '2201', color: '#eab308' },
  { label: 'Bro-led', code: '2301', color: '#ca8a04' },
  { label: 'Implantatkr.', code: '2401', color: '#a855f7' },
  { label: 'Tandrensning', code: '1301', color: '#06b6d4' },
  { label: 'Forebyggelse', code: '2920', color: '#0891b2' },
  { label: 'MH-I', code: '1302', color: '#0e7490' },
  { label: 'EXT', code: '1701', color: '#ef4444' },
  { label: 'EXT op', code: '1801', color: '#dc2626' },
  { label: 'Forsegling', code: '4100', color: '#84cc16' },
] as const;

// ── Sortable row ──────────────────────────────────────────────────────────────

interface SortableShortcutRowProps {
  sc: DentalShortcutRow;
  codeName?: string;
  onEdit: (sc: DentalShortcutRow) => void;
  onDelete: (sc: DentalShortcutRow) => void;
}

function SortableShortcutRow({ sc, codeName, onEdit, onDelete }: SortableShortcutRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: sc.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400 touch-none"
        aria-label="Træk for at omarrangere"
      >
        <GripVertical className="h-5 w-5" />
      </button>

      <div
        className="h-5 w-5 rounded shrink-0"
        style={{ backgroundColor: sc.color }}
        aria-hidden="true"
      />

      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-900 dark:text-white">{sc.label}</p>
        <p className="text-xs text-gray-400 dark:text-gray-500">
          {sc.procedure_code}
          {codeName ? ` — ${codeName}` : ''}
        </p>
      </div>

      {!sc.is_active && (
        <span className="rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs text-gray-400 dark:text-gray-500">
          Inaktiv
        </span>
      )}

      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={() => onEdit(sc)}
          className="rounded p-1.5 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
          title="Redigér"
        >
          <Pencil className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(sc)}
          className="rounded p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
          title="Slet"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function ClinicDentalShortcuts() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin } = useAuth();
  const isAdmin = activeClinicId ? isClinicAdmin(activeClinicId) : false;

  const [showCreate, setShowCreate] = useState(false);
  const [editingShortcut, setEditingShortcut] = useState<DentalShortcutRow | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const { data: shortcuts, isLoading } = useAllShortcuts(activeClinicId);
  const { data: codes } = useProcedureCodes();
  const upsert = useUpsertShortcut(activeClinicId);
  const deleteShortcut = useDeleteShortcut(activeClinicId);
  const reorder = useReorderShortcuts(activeClinicId);

  const codeMap = new Map(codes?.map((c) => [c.code, c.name_da]) ?? []);
  const existingLabels = new Set(shortcuts?.map((s) => s.label) ?? []);

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !shortcuts) return;

    const oldIndex = shortcuts.findIndex((s) => s.id === active.id);
    const newIndex = shortcuts.findIndex((s) => s.id === over.id);
    const reordered = arrayMove(shortcuts, oldIndex, newIndex);

    const orders = reordered.map((s: DentalShortcutRow, i: number) => ({ id: s.id, display_order: i + 1 }));
    try {
      await reorder.mutateAsync(orders);
    } catch {
      toast.error('Kunne ikke gemme rækkefølge');
    }
  };

  const handleSave = async (values: {
    id?: string;
    label: string;
    procedureCode: string;
    color: string;
    displayOrder?: number;
    isActive: boolean;
  }) => {
    await upsert.mutateAsync({
      id: values.id,
      label: values.label,
      procedureCode: values.procedureCode,
      color: values.color,
      displayOrder: values.displayOrder,
      isActive: values.isActive,
    });
    toast.success(values.id ? t('settings.dental.shortcuts.updated') : t('settings.dental.shortcuts.created'));
    setShowCreate(false);
    setEditingShortcut(null);
  };

  const handleDelete = async (sc: DentalShortcutRow) => {
    if (!confirm(t('settings.dental.shortcuts.confirmDelete', { label: sc.label }))) return;
    await deleteShortcut.mutateAsync(sc.id);
    toast.success(t('settings.dental.shortcuts.deleted'));
  };

  const handleImportDefaults = async () => {
    if (!activeClinicId) return;
    setIsImporting(true);
    try {
      let added = 0;
      for (const def of DEFAULT_SHORTCUTS) {
        if (!existingLabels.has(def.label)) {
          await upsert.mutateAsync({
            label: def.label,
            procedureCode: def.code,
            color: def.color,
            displayOrder: (shortcuts?.length ?? 0) + added + 1,
          });
          added++;
        }
      }
      if (added > 0) {
        toast.success(t('settings.dental.shortcuts.importedCount', { count: added }));
      } else {
        toast(t('settings.dental.shortcuts.importedNone'));
      }
    } catch {
      toast.error(t('settings.dental.shortcuts.importError'));
    } finally {
      setIsImporting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
        <p className="text-sm text-gray-400 dark:text-gray-500">
          {t('settings.dental.shortcuts.adminOnly', 'Kun klinikadmin')}
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('settings.dental.shortcuts.title')}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('settings.dental.shortcuts.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={handleImportDefaults}
            disabled={isImporting}
            className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            {isImporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {t('settings.dental.shortcuts.importDefaults')}
          </button>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            {t('settings.dental.shortcuts.addNew')}
          </button>
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : !shortcuts || shortcuts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
          <p className="text-sm text-gray-400 dark:text-gray-500">
            {t('settings.dental.shortcuts.emptyState')}
          </p>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={shortcuts.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {shortcuts.map((sc) => (
                <SortableShortcutRow
                  key={sc.id}
                  sc={sc}
                  codeName={codeMap.get(sc.procedure_code)}
                  onEdit={setEditingShortcut}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Create modal */}
      {showCreate && (
        <ShortcutEditModal
          onSave={handleSave}
          onClose={() => setShowCreate(false)}
          isSaving={upsert.isPending}
        />
      )}

      {/* Edit modal */}
      {editingShortcut && (
        <ShortcutEditModal
          initial={editingShortcut}
          onSave={handleSave}
          onClose={() => setEditingShortcut(null)}
          isSaving={upsert.isPending}
        />
      )}
    </div>
  );
}
